import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Plus, Layers, Truck, X, CheckCircle, Clock, Grid3x3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

/**
 * Slice D9 (Phase 1) — Per-section ComboV2 Plans + Distribution skeleton.
 *
 * Salem-untouched: lives entirely in DispatchPerSection. Uses
 * /api/per-section/{slug}/combo-v2-plans/... endpoints.
 *
 * Scope: list plans, create new plan from a flat bundle list, view detail,
 * cancel (only DRAFT/PICKING), distribute to a PP table. Matrix planner /
 * PP scan / return-excess / reversal will land in D9.1+.
 */

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo Section',
  art_silk: 'Art Silk Section',
  kids: 'Kids Section',
};

const STATUS_BADGE = {
  DRAFT: 'bg-gray-200 text-gray-800',
  PICKING: 'bg-amber-100 text-amber-800',
  DISTRIBUTED: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
};

const PerSectionComboV2Clone = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug] || slug;
  const backRoute = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/combo-v2-plans`;

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createMode, setCreateMode] = useState('flat'); // 'flat' | 'matrix'
  const [planName, setPlanName] = useState('');
  const [planDescription, setPlanDescription] = useState('');
  const [bundleEntries, setBundleEntries] = useState([{ bundle_code: '', sku_code: '', target_qty: '' }]);
  const [submitting, setSubmitting] = useState(false);

  // D9.1 Matrix planner state
  const [lookupOptions, setLookupOptions] = useState({ rows: [], sizes: [] });
  const [matrixRows, setMatrixRows] = useState([
    { design_name: '', shade: '', description: '', sizes: {} },
  ]);

  const [distributeModal, setDistributeModal] = useState({ open: false, plan: null });
  const [ppTableNo, setPpTableNo] = useState('');
  const [handoverStaffId, setHandoverStaffId] = useState('');
  const [distributionRemarks, setDistributionRemarks] = useState('');

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(apiBase);
      setPlans(response.data.plans || []);
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const fetchLookup = useCallback(async () => {
    try {
      const response = await apiClient.get(`${apiBase}/lookup/options`);
      setLookupOptions({
        rows: response.data.rows || [],
        sizes: response.data.sizes || [],
      });
    } catch (error) {
      // Lookup is optional — Matrix tab simply shows empty datalists.
    }
  }, [apiBase]);

  useEffect(() => {
    if (createMode === 'matrix' && showCreate) {
      fetchLookup();
    }
  }, [createMode, showCreate, fetchLookup]);

  const handleAddBundleRow = () => {
    setBundleEntries((prev) => [...prev, { bundle_code: '', sku_code: '', target_qty: '' }]);
  };
  const handleRemoveBundleRow = (idx) => {
    setBundleEntries((prev) => prev.filter((_, i) => i !== idx));
  };
  const handleBundleEntryChange = (idx, field, value) => {
    setBundleEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));
  };

  const resetCreateForm = () => {
    setPlanName('');
    setPlanDescription('');
    setBundleEntries([{ bundle_code: '', sku_code: '', target_qty: '' }]);
    setMatrixRows([{ design_name: '', shade: '', description: '', sizes: {} }]);
    setShowCreate(false);
  };

  const handleAddMatrixRow = () => {
    setMatrixRows((prev) => [...prev, { design_name: '', shade: '', description: '', sizes: {} }]);
  };
  const handleRemoveMatrixRow = (idx) => {
    setMatrixRows((prev) => prev.filter((_, i) => i !== idx));
  };
  const handleMatrixCellChange = (idx, field, value) => {
    setMatrixRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const handleMatrixSizeChange = (idx, size, value) => {
    setMatrixRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const next = { ...r.sizes };
        const n = Number(value);
        if (!value || Number.isNaN(n) || n <= 0) {
          delete next[size];
        } else {
          next[size] = n;
        }
        return { ...r, sizes: next };
      })
    );
  };

  const handleCreateMatrixPlan = async () => {
    const cleaned = matrixRows
      .map((r) => ({
        design_name: r.design_name.trim(),
        shade: r.shade.trim(),
        description: r.description.trim(),
        sizes: r.sizes || {},
      }))
      .filter((r) => r.design_name && r.shade && Object.keys(r.sizes).length > 0);

    if (cleaned.length === 0) {
      toast.error('Add at least one matrix row with design, shade, and size qty');
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiClient.post(`${apiBase}/from-matrix`, {
        plan_name: planName || undefined,
        description: planDescription || undefined,
        matrix: cleaned,
      });
      const summary = response.data.summary;
      toast.success(
        `Matrix plan created — ${summary.sessions_created} sessions, ${summary.slips_created} slips` +
          (summary.shortfalls?.length ? ` (${summary.shortfalls.length} shortfalls)` : '')
      );
      resetCreateForm();
      fetchPlans();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.unresolved) {
        toast.error(
          `${detail.message} (${detail.unresolved.length} unresolved cells)`
        );
      } else {
        toast.error(detail || 'Failed to create matrix plan');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreatePlan = async () => {
    const cleaned = bundleEntries
      .map((e) => ({
        bundle_code: e.bundle_code.trim(),
        sku_code: e.sku_code.trim() || undefined,
        target_qty: e.target_qty ? Number(e.target_qty) : undefined,
      }))
      .filter((e) => e.bundle_code.length > 0);

    if (cleaned.length === 0) {
      toast.error('Please add at least one bundle');
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(apiBase, {
        plan_name: planName || undefined,
        description: planDescription || undefined,
        bundles: cleaned,
      });
      toast.success(`Plan created with ${cleaned.length} bundles`);
      resetCreateForm();
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create plan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelPlan = async (plan) => {
    if (!window.confirm(`Cancel plan ${plan.plan_id}?`)) return;
    try {
      await apiClient.post(`${apiBase}/${plan.plan_id}/cancel`, { reason: 'Cancelled by operator' });
      toast.success(`Plan ${plan.plan_id} cancelled`);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cancel failed');
    }
  };

  const openDistribute = (plan) => {
    setDistributeModal({ open: true, plan });
    setPpTableNo('');
    setHandoverStaffId('');
    setDistributionRemarks('');
  };

  const closeDistribute = () => {
    setDistributeModal({ open: false, plan: null });
  };

  const handleConfirmDistribute = async () => {
    if (!ppTableNo.trim() || !handoverStaffId.trim()) {
      toast.error('PP Table and Handover Staff ID are required');
      return;
    }
    try {
      await apiClient.post(
        `${apiBase}/${distributeModal.plan.plan_id}/distribute`,
        {
          pp_table_no: ppTableNo,
          handed_over_to_staff_id: handoverStaffId,
          remarks: distributionRemarks || undefined,
        },
      );
      toast.success(`Distributed to PP Table ${ppTableNo}`);
      closeDistribute();
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Distribute failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid={`per-section-combo-v2-${slug}`}>
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(backRoute)}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">ComboV2 Plans</h1>
              <p className="text-sm text-[#E8DCC8]">{sectionName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/sections/dispatch/${slug}/combo-v2-planner`)}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="open-matrix-planner-btn"
            >
              <Grid3x3 className="w-4 h-4 mr-2" />
              Matrix Planner
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPlans}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => setShowCreate((v) => !v)}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
              data-testid="toggle-create-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {showCreate ? 'Close' : 'New Plan'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {showCreate && (
          <Card className="border-fuchsia-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-fuchsia-700">
                <Layers className="w-5 h-5" />
                Create New Plan
              </CardTitle>
              <CardDescription>
                {createMode === 'matrix'
                  ? 'Pick designs/shades from the BOM master, set qty per size; the system FIFO-allocates bundles + creates pick slips.'
                  : 'Add bundles that should be combined together. Bundles must be in this section\u2019s SFG inventory and not already in another active plan.'}
              </CardDescription>
              <div className="flex items-center gap-2 pt-2">
                <Button
                  size="sm"
                  variant={createMode === 'flat' ? 'default' : 'outline'}
                  onClick={() => setCreateMode('flat')}
                  className={createMode === 'flat' ? 'bg-fuchsia-600 hover:bg-fuchsia-700 text-white' : ''}
                  data-testid="mode-flat-btn"
                >
                  Flat list
                </Button>
                <Button
                  size="sm"
                  variant={createMode === 'matrix' ? 'default' : 'outline'}
                  onClick={() => setCreateMode('matrix')}
                  className={createMode === 'matrix' ? 'bg-fuchsia-600 hover:bg-fuchsia-700 text-white' : ''}
                  data-testid="mode-matrix-btn"
                >
                  Matrix planner (D9.1)
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Plan Name (Optional)</Label>
                  <Input
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                    placeholder="e.g. Spring Combo Run #4"
                    data-testid="plan-name-input"
                  />
                </div>
                <div>
                  <Label>Description (Optional)</Label>
                  <Input
                    value={planDescription}
                    onChange={(e) => setPlanDescription(e.target.value)}
                    placeholder="Notes for this plan"
                    data-testid="plan-description-input"
                  />
                </div>
              </div>

              {createMode === 'flat' && (
                <div>
                <Label>Bundles</Label>
                <div className="space-y-2 mt-2">
                  {bundleEntries.map((entry, idx) => (
                    <div key={idx} className="flex gap-2" data-testid={`bundle-row-${idx}`}>
                      <Input
                        value={entry.bundle_code}
                        onChange={(e) => handleBundleEntryChange(idx, 'bundle_code', e.target.value)}
                        placeholder="Bundle code"
                        className="flex-1"
                        data-testid={`bundle-code-${idx}`}
                      />
                      <Input
                        value={entry.sku_code}
                        onChange={(e) => handleBundleEntryChange(idx, 'sku_code', e.target.value)}
                        placeholder="SKU (optional)"
                        className="w-40"
                        data-testid={`bundle-sku-${idx}`}
                      />
                      <Input
                        value={entry.target_qty}
                        onChange={(e) => handleBundleEntryChange(idx, 'target_qty', e.target.value)}
                        placeholder="Qty"
                        type="number"
                        className="w-24"
                        data-testid={`bundle-qty-${idx}`}
                      />
                      {bundleEntries.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveBundleRow(idx)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`remove-bundle-${idx}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddBundleRow}
                  className="mt-2"
                  data-testid="add-bundle-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add bundle
                </Button>
                </div>
              )}

              {createMode === 'matrix' && (
                <div>
                  <Label>Design × Shade × Size Matrix</Label>
                  <p className="text-xs text-gray-500 mt-1">
                    Lookup options: {lookupOptions.rows.length} BOM combos.
                  </p>
                  <div className="space-y-3 mt-2">
                    {matrixRows.map((row, idx) => {
                      // Suggest sizes available in BOM for this design+shade.
                      const matched = lookupOptions.rows.find(
                        (r) =>
                          r.design_name === row.design_name &&
                          r.shade === row.shade &&
                          (r.description || '') === (row.description || '')
                      );
                      const suggestedSizes = matched?.sizes_available || lookupOptions.sizes || [];
                      return (
                        <div
                          key={idx}
                          className="border rounded p-3 bg-gray-50 space-y-2"
                          data-testid={`matrix-row-${idx}`}
                        >
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-xs">Design</Label>
                              <Input
                                list={`design-options-${slug}`}
                                value={row.design_name}
                                onChange={(e) => handleMatrixCellChange(idx, 'design_name', e.target.value)}
                                placeholder="design_name"
                                data-testid={`matrix-design-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Shade</Label>
                              <Input
                                value={row.shade}
                                onChange={(e) => handleMatrixCellChange(idx, 'shade', e.target.value)}
                                placeholder="shade"
                                data-testid={`matrix-shade-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Description</Label>
                              <Input
                                value={row.description}
                                onChange={(e) => handleMatrixCellChange(idx, 'description', e.target.value)}
                                placeholder="optional"
                                data-testid={`matrix-desc-${idx}`}
                              />
                            </div>
                          </div>

                          <div>
                            <Label className="text-xs">Quantity per size</Label>
                            <div className="grid grid-cols-5 gap-2 mt-1">
                              {suggestedSizes.map((size) => (
                                <div key={size} className="flex flex-col">
                                  <span className="text-xs text-gray-500 text-center">{size}</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={row.sizes?.[size] || ''}
                                    onChange={(e) => handleMatrixSizeChange(idx, size, e.target.value)}
                                    placeholder="0"
                                    className="text-center"
                                    data-testid={`matrix-size-${idx}-${size}`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          {matrixRows.length > 1 && (
                            <div className="flex justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveMatrixRow(idx)}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`remove-matrix-${idx}`}
                              >
                                <X className="w-4 h-4 mr-1" /> Remove row
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddMatrixRow}
                    className="mt-2"
                    data-testid="add-matrix-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add design row
                  </Button>

                  <datalist id={`design-options-${slug}`}>
                    {lookupOptions.rows.map((r) => (
                      <option key={`${r.design_name}|${r.shade}`} value={r.design_name} />
                    ))}
                  </datalist>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={resetCreateForm} className="flex-1" data-testid="cancel-create-btn">
                  Cancel
                </Button>
                <Button
                  onClick={createMode === 'matrix' ? handleCreateMatrixPlan : handleCreatePlan}
                  disabled={submitting}
                  className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                  data-testid="submit-plan-btn"
                >
                  {submitting ? 'Creating\u2026' : createMode === 'matrix' ? 'Create Matrix Plan' : 'Create Plan'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-indigo-500" />
              Plans ({plans.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {plans.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                No plans yet. Click <strong>New Plan</strong> to create one.
              </p>
            ) : (
              <div className="space-y-3">
                {plans.map((plan) => {
                  const status = (plan.status || '').toUpperCase();
                  const canCancel = !['DISTRIBUTED', 'COMPLETED', 'CANCELLED'].includes(status);
                  const canDistribute = status === 'DRAFT' || status === 'PICKING';
                  return (
                    <div
                      key={plan.plan_id}
                      className="p-4 border rounded-lg flex items-center justify-between bg-white hover:bg-gray-50"
                      data-testid={`plan-row-${plan.plan_id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-semibold text-[#1A1F2E]">{plan.plan_id}</span>
                          <Badge className={STATUS_BADGE[status] || 'bg-gray-100 text-gray-700'}>
                            {status}
                          </Badge>
                          {plan.bundle_count != null && (
                            <span className="text-xs text-gray-500">
                              {plan.bundle_count} bundles
                            </span>
                          )}
                        </div>
                        {plan.plan_name && plan.plan_name !== plan.plan_id && (
                          <p className="text-sm font-medium text-[#1A1F2E]">{plan.plan_name}</p>
                        )}
                        {plan.description && (
                          <p className="text-xs text-gray-500 mt-1">{plan.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          <Clock className="w-3 h-3" />
                          {plan.created_at ? new Date(plan.created_at).toLocaleString() : '—'}
                          {plan.distributed_at && (
                            <>
                              <span>·</span>
                              <span className="text-blue-600">
                                Distributed → PP Table {plan.pp_table_no || '—'}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {canDistribute && (
                          <Button
                            size="sm"
                            onClick={() => openDistribute(plan)}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid={`distribute-${plan.plan_id}`}
                          >
                            <Truck className="w-4 h-4 mr-1" />
                            Distribute
                          </Button>
                        )}
                        {canCancel && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCancelPlan(plan)}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            data-testid={`cancel-${plan.plan_id}`}
                          >
                            Cancel
                          </Button>
                        )}
                        {!canDistribute && !canCancel && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {distributeModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-600" />
                Distribute Plan
              </CardTitle>
              <CardDescription>
                Hand over plan <span className="font-mono">{distributeModal.plan?.plan_id}</span> to a PP table.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>PP Table Number *</Label>
                <Input
                  value={ppTableNo}
                  onChange={(e) => setPpTableNo(e.target.value)}
                  placeholder="e.g. T-04"
                  data-testid="pp-table-input"
                />
              </div>
              <div>
                <Label>Handover Staff ID *</Label>
                <Input
                  value={handoverStaffId}
                  onChange={(e) => setHandoverStaffId(e.target.value)}
                  placeholder="Receiving staff at PP table"
                  data-testid="handover-staff-input"
                />
              </div>
              <div>
                <Label>Remarks (Optional)</Label>
                <Textarea
                  value={distributionRemarks}
                  onChange={(e) => setDistributionRemarks(e.target.value)}
                  rows={2}
                  data-testid="distribution-remarks-input"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={closeDistribute} className="flex-1" data-testid="close-distribute-btn">
                  Close
                </Button>
                <Button
                  onClick={handleConfirmDistribute}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="confirm-distribute-btn"
                >
                  Confirm Distribute
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PerSectionComboV2Clone;
